import { KeenIcon } from '@/components';

export interface DashboardStatCardProps {
  title: string;
  value: number;
  description: string;
  icon: string;
  loading?: boolean;
  variant?: 'default' | 'primary';
  highlight?: boolean;
  /** When set, shown instead of numeric value (e.g. formatted currency). */
  displayValue?: string;
  /** When set, the whole card is a button, e.g. to drill down into the records behind the number. */
  onClick?: () => void;
}

export const DashboardStatCard = ({
  title,
  value,
  description,
  icon,
  loading,
  variant = 'default',
  highlight = false,
  displayValue,
  onClick,
}: DashboardStatCardProps) => {
  const baseCardClasses =
    'rounded-lg border border-gray-500 text-gray-900 shadow-md p-6';

  const primaryClasses = 'bg-primary text-white border-none';
  const highlightRing = highlight ? ' ring-2 ring-primary/40' : '';

  const cardClasses =
    variant === 'primary'
      ? `${baseCardClasses} ${primaryClasses}${highlightRing}`
      : `${baseCardClasses}${highlightRing}`;

  const titleClasses =
    'text-xs uppercase tracking-wide ' +
    (variant === 'primary' ? 'text-white' : 'text-gray-600');

  const valueClasses =
    'text-2xl font-semibold ' +
    (variant === 'primary' ? 'text-white' : 'text-gray-900');

  const descriptionClasses =
    'text-[11px] mt-1 ' +
    (variant === 'primary' ? 'text-white' : 'text-gray-600');

  const iconWrapperClasses =
    'h-10 w-10 rounded-full flex items-center justify-center ' +
    (variant === 'primary' ? 'bg-white/20' : 'bg-primary/10');

  const iconClasses =
    variant === 'primary' ? 'text-white text-lg' : 'text-primary text-lg';

  const content = (
    <div className="flex items-center justify-between gap-4">
      <div className="flex flex-col">
        <span className={titleClasses}>{title}</span>
        <span className={valueClasses}>
          {loading ? '-' : displayValue ?? value}
        </span>
        <span className={descriptionClasses}>{description}</span>
      </div>
      <div className={iconWrapperClasses}>
        <KeenIcon icon={icon} className={iconClasses} />
      </div>
    </div>
  );

  if (onClick) {
    return (
      <button
        type="button"
        className={`${cardClasses} w-full text-left cursor-pointer transition-shadow hover:shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-primary`}
        onClick={onClick}
        disabled={loading}
      >
        {content}
      </button>
    );
  }

  return <div className={cardClasses}>{content}</div>;
};
